import { Failure, Result } from '@/usecases/repository';
import { Query, QueryFilter } from '@/usecases/usecases';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { TopicoRepository, TopicoState } from '../core/topico_repository';

class TopicoRepositoryImpl implements TopicoRepository {
  private readonly _remoteDataSource: RemoteDataSource;

  constructor() {
    this._remoteDataSource = new RemoteDataSource(Settings.urlBase);
  }

  async obterTopico(query: Query): Promise<Result<Record<string, unknown>, Failure>> {
    const topicoId = query.get('topicoId');

    if (!topicoId) {
      return Result.failure(new Failure({ messages: ['ID do tópico é obrigatório'] }));
    }

    const result = await this._remoteDataSource.get<Record<string, unknown>>({
      endpoint: `v1/topicos/${topicoId}`,
      requiredToken: true,
      serialize: (map) => map as Record<string, unknown>,
      headers: { Accept: 'application/vnd.data.full.v1+json' },
      forceRefresh: true,
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }
    return Result.success(result.success.data!);
  }

  async obterTopicos(query: QueryFilter): Promise<Result<unknown[], Failure>> {
    const result = await this._remoteDataSource.get<unknown[]>({
      endpoint: 'v1/topicos',
      queryParams: query.queryParams,
      headers: { accept: query.accept || 'application/vnd.data.compact.v1+json' },
      forceRefresh: true,
      requiredToken: true,
      serialize: (map) => map as unknown[],
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }
    return Result.success(result.success.data!);
  }

  async adicionarTopico(state: TopicoState): Promise<Result<string, Failure>> {
    const body: Record<string, unknown> = {
      descricao: state.descricao,
      etiquetas: state.etiquetas,
      tipo: state.tipo,
      status: state.status,
      contexto: state.contexto,
      metodoValidacao: state.metodoValidacao,
      municipioId: state.municipioId,
    };

    const result = await this._remoteDataSource.post<string>({
      endpoint: 'v1/topicos',
      body,
      requiredToken: true,
      headers: { accept: 'application/vnd.data.minimal.v1+json' },
      serialize: (map) => (map as { id?: string }).id ?? '',
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }

    return Result.success(result.success.data ?? '');
  }

  async atualizarTopico(query: Query, state: TopicoState): Promise<Result<TopicoState, Failure>> {
    const topicoId = query.get('topicoId');

    if (!topicoId) {
      return Result.failure(new Failure({ messages: ['ID do tópico é obrigatório'] }));
    }

    const result = await this._remoteDataSource.put<Record<string, unknown>>({
      endpoint: `v1/topicos/${topicoId}`,
      body: state as Record<string, unknown>,
      requiredToken: true,
      serialize: (map) => map as Record<string, unknown>,
      headers: { accept: 'application/vnd.data.full.v1+json' },
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }

    return Result.success(result.success.data as TopicoState);
  }

  async atualizarTopicoPatch(query: Query, operations: Array<{ op: string; path: string; value: unknown }>): Promise<Result<TopicoState, Failure>> {
    const topicoId = query.get('topicoId');

    if (!topicoId) {
      return Result.failure(new Failure({ messages: ['ID do tópico é obrigatório'] }));
    }

    const result = await this._remoteDataSource.patch<Record<string, unknown>>({
      endpoint: `v1/topicos/${topicoId}`,
      body: operations,
      requiredToken: true,
      serialize: (map) => map as Record<string, unknown>,
      headers: {
        Accept: 'application/vnd.data.full.v1+json',
        'content-type': 'application/json-patch+json',
      },
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }

    return Result.success(result.success.data as TopicoState);
  }

  async arquivarTopico(query: Query): Promise<Result<TopicoState, Failure>> {
    const topicoId = query.get('topicoId');

    if (!topicoId) {
      return Result.failure(new Failure({ messages: ['ID do tópico é obrigatório'] }));
    }

    const result = await this._remoteDataSource.post<Record<string, unknown>>({
      endpoint: `v1/topicos/${topicoId}/arquivar`,
      requiredToken: true,
      serialize: (map) => map as Record<string, unknown>,
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }

    return Result.success(result.success.data as TopicoState);
  }

  async restaurarTopico(query: Query): Promise<Result<TopicoState, Failure>> {
    const topicoId = query.get('topicoId');

    if (!topicoId) {
      return Result.failure(new Failure({ messages: ['ID do tópico é obrigatório'] }));
    }

    const result = await this._remoteDataSource.post<Record<string, unknown>>({
      endpoint: `v1/topicos/${topicoId}/restaurar`,
      requiredToken: true,
      serialize: (map) => map as Record<string, unknown>,
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }

    return Result.success(result.success.data as TopicoState);
  }

  async removerTopico(query: Query): Promise<Result<boolean, Failure>> {
    const topicoId = query.get('topicoId');

    if (!topicoId) {
      return Result.failure(new Failure({ messages: ['ID do tópico é obrigatório'] }));
    }

    const result = await this._remoteDataSource.delete({
      endpoint: `v1/topicos/${topicoId}`,
      requiredToken: true,
    });

    if ('failure' in result) {
      return Result.failure(result.failure);
    }

    return Result.success(result.success.data ?? true);
  }
}

export { TopicoRepositoryImpl };
